import { create } from 'zustand';
import { api } from '../lib/api-client';
import type { Submission, SubmissionComment, SubmissionStatus } from '@spine-workspace/shared';

interface SubmissionState {
  submissions: Submission[];
  loading: boolean;
  error: string | null;

  // Comments keyed by submission ID
  comments: Record<string, SubmissionComment[]>;
  commentsLoading: Record<string, boolean>;

  fetchSubmissions: () => Promise<void>;
  submitForReview: (params: { driveFolderId: string; driveFolderName?: string; assetId?: string; name?: string; note?: string }) => Promise<Submission>;
  updateStatus: (id: string, status: SubmissionStatus, notes?: string) => Promise<void>;
  fetchComments: (submissionId: string) => Promise<void>;
  addComment: (submissionId: string, message: string, annotationId?: string, versionNumber?: number) => Promise<void>;
}

export const useSubmissionStore = create<SubmissionState>((set) => ({
  submissions: [],
  loading: false,
  error: null,
  comments: {},
  commentsLoading: {},

  fetchSubmissions: async () => {
    set({ loading: true, error: null });
    try {
      const res = await api.get<{ submissions: Submission[] }>('/api/submissions');
      set({ submissions: res.submissions });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load submissions' });
    } finally {
      set({ loading: false });
    }
  },

  submitForReview: async ({ driveFolderId, driveFolderName, assetId, name, note }) => {
    const res = await api.post<{ submission: Submission; isNew: boolean }>(
      '/api/submissions',
      { driveFolderId, driveFolderName, assetId, name, note }
    );
    const sub = res.submission;
    set(s => ({
      submissions: s.submissions.some(x => x.id === sub.id)
        ? s.submissions.map(x => x.id === sub.id ? sub : x)
        : [sub, ...s.submissions],
    }));
    return sub;
  },

  updateStatus: async (id, status, notes) => {
    const res = await api.patch<{ submission: Submission }>(`/api/submissions/${id}/status`, { status, notes });
    const sub = res.submission;
    set(s => ({
      submissions: s.submissions.map(x => x.id === sub.id ? sub : x),
    }));
  },

  fetchComments: async (submissionId) => {
    set(s => ({ commentsLoading: { ...s.commentsLoading, [submissionId]: true } }));
    try {
      const res = await api.get<{ comments: SubmissionComment[] }>(`/api/submissions/${submissionId}/comments`);
      set(s => ({ comments: { ...s.comments, [submissionId]: res.comments } }));
    } finally {
      set(s => ({ commentsLoading: { ...s.commentsLoading, [submissionId]: false } }));
    }
  },

  addComment: async (submissionId, message, annotationId, versionNumber) => {
    const res = await api.post<{ comment: SubmissionComment }>(
      `/api/submissions/${submissionId}/comments`,
      { message, annotationId, ...(versionNumber != null ? { versionNumber } : {}) }
    );
    const comment = res.comment;
    set(s => ({
      comments: {
        ...s.comments,
        [submissionId]: [...(s.comments[submissionId] ?? []), comment],
      },
    }));
  },
}));
